import styled, { keyframes } from 'styled-components';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-15px) rotate(120deg);
  }
  66% {
    transform: translateY(8px) rotate(240deg);
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Main Container
export const AboutContainer = styled.div`
  min-height: calc(100vh - 80px);
  background: #ffffff;
  position: relative;
  overflow-x: hidden;
`;

// Floating Elements
export const FloatingElement = styled.div`
  position: absolute;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, rgba(43, 56, 87, 0.08), rgba(197, 51, 123, 0.05));
  border-radius: 50%;
  animation: ${float} 20s ease-in-out infinite;
  animation-delay: ${props => props.delay};
  z-index: 1;
  
  &:nth-child(2) {
    top: 20%;
    right: 10%;
    width: 60px;
    height: 60px;
  }
  
  &:nth-child(3) {
    top: 60%;
    left: 5%;
    width: 100px;
    height: 100px;
  }
  
  &:nth-child(4) {
    bottom: 20%;
    right: 20%;
    width: 70px;
    height: 70px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// Main Content
export const MainContent = styled.main`
  padding: 2rem;
  position: relative;
  z-index: 5;
  max-width: 1200px;
  margin: 0 auto;
  animation: ${fadeInUp} 1s ease-out 0.3s both;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Section Header
export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

export const Title = styled.h1`
  color: #2B3857;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  text-shadow: 0 2px 4px rgba(43, 56, 87, 0.2);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const Subtitle = styled.p`
  color: #C5337B;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// Form
export const Form = styled.form`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(43, 56, 87, 0.1);
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(43, 56, 87, 0.1);

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 15px;
  }
`;

export const FormSection = styled.div`
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h2`
  color: #2B3857;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 1.5rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(197, 51, 123, 0.2);
`;

// Input Groups
export const InputGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const Label = styled.label`
  display: block;
  color: #2B3857;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 1rem;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(43, 56, 87, 0.2);
  border-radius: 8px;
  font-size: 1rem;
  color: #2B3857;
  background: rgba(248, 250, 252, 0.8);
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #C5337B;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 0 0 3px rgba(197, 51, 123, 0.1);
  }

  &::placeholder {
    color: rgba(43, 56, 87, 0.5);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid rgba(43, 56, 87, 0.2);
  border-radius: 8px;
  font-size: 1rem;
  color: #2B3857;
  background: rgba(248, 250, 252, 0.8);
  transition: all 0.3s ease;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #C5337B;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 0 0 3px rgba(197, 51, 123, 0.1);
  }

  &::placeholder {
    color: rgba(43, 56, 87, 0.5);
  }
`;

// Statistics Grid
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(43, 56, 87, 0.15);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(43, 56, 87, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${props => props.color};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 15px ${props => props.color}40;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

// Submit Button
export const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #2B3857 0%, #1e293b 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(43, 56, 87, 0.4);
    
    &:before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }
`;

// Loading Spinner
export const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

// Messages
export const ErrorMessage = styled.div`
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const SuccessMessage = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #22c55e;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Preview Section
export const PreviewSection = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(43, 56, 87, 0.15);
`;

export const PreviewTitle = styled.h2`
  color: #2B3857;
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  text-align: center;
`;

export const PreviewCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(43, 56, 87, 0.15);
  border-radius: 12px;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const PreviewContent = styled.div`
  text-align: left;
`;

export const PreviewStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(43, 56, 87, 0.1);

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

export const PreviewStat = styled.div`
  text-align: center;
  padding: 1rem;
  background: rgba(43, 56, 87, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(43, 56, 87, 0.1);
`;

export const PreviewStatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #C5337B;
  margin-bottom: 0.25rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

export const PreviewStatLabel = styled.div`
  font-size: 0.9rem;
  color: #64748B;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;
